import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor,
  useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  ArrowLeft, Trash2, Play, Pencil, Check, X,
  Plus, ListOrdered, GripVertical, FileText,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GAME_LABELS = {
  la_course: 'La Course', la_tour: 'La Tour', robot_balayeur: 'Robot Balayeur',
  tri_express: 'Tri Express', batisseur: 'Batisseur', detective: 'Le Detective',
  memoire: 'La Memoire', avalanche: "L'Avalanche", le_chef: 'Le Chef',
  carte_du_canada: 'Carte du Canada', duel_conjugaison: 'Le Duel',
  horloge_folle: "L'Horloge Folle",
};

const MODE_LABELS = { school: 'School Year', level: 'By Level', topic: 'By Topic' };
const LEVEL_LABELS = { 'pre-A1': 'Beginner', 'A1': 'Elementary', 'A1-A2': 'Intermediate', 'A2': 'Upper Intermediate' };

function SortableItem({ item, index, totalItems, expandedItem, setExpandedItem, editingLabel, setEditingLabel, labelValue, setLabelValue, saveLabel, removeItem, launchPreset }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: item.item_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 'auto',
    position: 'relative',
  };

  const preset = item.preset;
  const isExpanded = expandedItem === item.item_id;
  const isEditingLabel = editingLabel === item.item_id;
  const displayLabel = item.custom_label || `Day ${index + 1}`;
  const presetName = preset?.name || '(deleted preset)';
  const gameName = preset ? (GAME_LABELS[preset.game] || preset.game) : '';
  const config = preset?.config || {};

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`playlist-item-${item.item_id}`}
    >
      <div style={{
        background: isDragging ? '#F8FAFF' : 'white',
        borderRadius: '0.875rem',
        border: isDragging ? '2px solid #4C6EF5' : '1px solid #E2E8F0',
        overflow: 'hidden',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        boxShadow: isDragging ? '0 12px 28px rgba(76,110,245,0.18)' : 'none',
      }}>
        {/* Item row */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem',
          padding: '0.875rem 1.25rem',
        }}>
          {/* Drag handle */}
          <div
            data-testid={`playlist-item-drag-${item.item_id}`}
            {...attributes}
            {...listeners}
            style={{
              cursor: isDragging ? 'grabbing' : 'grab',
              color: '#CBD5E1', display: 'flex', alignItems: 'center',
              padding: '0.25rem', flexShrink: 0, touchAction: 'none',
            }}
          >
            <GripVertical size={18} />
          </div>

          {/* Day number badge */}
          <div style={{
            width: 36, height: 36, borderRadius: '0.5rem',
            background: '#F0F4FF', display: 'flex', alignItems: 'center',
            justifyContent: 'center', flexShrink: 0,
            fontFamily: 'Fredoka, sans-serif', fontSize: '0.9rem',
            fontWeight: 600, color: '#4C6EF5',
          }}>
            {index + 1}
          </div>

          {/* Label + preset name — clickable to expand */}
          <div
            style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
            onClick={() => setExpandedItem(isExpanded ? null : item.item_id)}
          >
            {isEditingLabel ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }} onClick={e => e.stopPropagation()}>
                <input
                  data-testid={`playlist-item-label-input-${item.item_id}`}
                  value={labelValue}
                  onChange={e => setLabelValue(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && saveLabel(item.item_id)}
                  placeholder={`Day ${index + 1}`}
                  autoFocus
                  style={{
                    fontFamily: 'Nunito, sans-serif', fontSize: '0.875rem', fontWeight: 600,
                    border: '1px solid #4C6EF5', borderRadius: '0.375rem',
                    padding: '0.2rem 0.5rem', outline: 'none', flex: 1, minWidth: 0,
                  }}
                />
                <button onClick={() => saveLabel(item.item_id)} style={iconBtnStyle}><Check size={14} color="#2F9E44" /></button>
                <button onClick={() => setEditingLabel(null)} style={iconBtnStyle}><X size={14} color="#E03131" /></button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <span style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 700, color: '#1A1D20', fontSize: '0.9rem' }}>
                  {displayLabel}
                </span>
                <button
                  data-testid={`playlist-item-edit-label-${item.item_id}`}
                  onClick={e => { e.stopPropagation(); setLabelValue(item.custom_label || ''); setEditingLabel(item.item_id); }}
                  style={{ ...iconBtnStyle, color: '#CBD5E1' }}
                >
                  <Pencil size={12} />
                </button>
              </div>
            )}
            <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.775rem', color: '#94A3B8', marginTop: '0.1rem' }}>
              {presetName}{gameName ? ` \u00B7 ${gameName}` : ''}
            </div>
          </div>

          {/* Game badge */}
          {preset && (
            <span style={{
              fontFamily: 'Nunito, sans-serif', fontSize: '0.7rem', fontWeight: 600,
              color: '#4C6EF5', background: '#F0F4FF', padding: '0.2rem 0.6rem',
              borderRadius: '1rem', whiteSpace: 'nowrap', flexShrink: 0,
            }}>
              {gameName}
            </span>
          )}

          {/* Delete */}
          <button
            data-testid={`playlist-item-remove-${item.item_id}`}
            onClick={e => { e.stopPropagation(); removeItem(item.item_id); }}
            style={{ ...iconBtnStyle, color: '#94A3B8' }}
          >
            <Trash2 size={14} />
          </button>
        </div>

        {/* Expanded details */}
        {isExpanded && preset && (
          <div style={{
            padding: '0.75rem 1.25rem 1rem', borderTop: '1px solid #F1F5F9',
            background: '#FAFBFC',
          }}>
            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '0.875rem' }}>
              {config.mode && <DetailChip label="Mode" value={MODE_LABELS[config.mode] || config.mode} />}
              {config.level && <DetailChip label="Level" value={LEVEL_LABELS[config.level] || config.level} />}
              {config.difficulty && <DetailChip label="Difficulty" value={config.difficulty} />}
              {config.participation && <DetailChip label="Participation" value={config.participation} />}
              {config.timer && config.timer !== '0' && <DetailChip label="Timer" value={`${config.timer}s`} />}
              {config.target && config.target !== '999' && <DetailChip label="Target" value={config.target} />}
            </div>
            <button
              data-testid={`playlist-item-launch-${item.item_id}`}
              onClick={() => launchPreset(preset)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                padding: '0.5rem 1.25rem', borderRadius: '0.5rem',
                background: '#1E2140', color: 'white', border: 'none',
                fontFamily: 'Nunito, sans-serif', fontSize: '0.825rem', fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              <Play size={14} fill="white" /> Launch Game
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function PlaylistDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [playlist, setPlaylist] = useState(null);
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingLabel, setEditingLabel] = useState(null);
  const [labelValue, setLabelValue] = useState('');
  const [expandedItem, setExpandedItem] = useState(null);
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [editingDesc, setEditingDesc] = useState(false);
  const [descValue, setDescValue] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const fetchPlaylist = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/teacher/playlists/${id}`);
      setPlaylist(res.data);
    } catch {
      navigate('/dashboard');
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchPlaylist().finally(() => setLoading(false));
  }, [fetchPlaylist]);

  const fetchPresets = async () => {
    const res = await axios.get(`${API_URL}/teacher/presets`);
    setPresets(res.data);
  };

  const addItem = async (presetId) => {
    await axios.post(`${API_URL}/teacher/playlists/${id}/items`, { preset_id: presetId });
    await fetchPlaylist();
  };

  const removeItem = async (itemId) => {
    await axios.delete(`${API_URL}/teacher/playlists/${id}/items/${itemId}`);
    await fetchPlaylist();
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const items = playlist.items;
    const oldIndex = items.findIndex(i => i.item_id === active.id);
    const newIndex = items.findIndex(i => i.item_id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    // Optimistic update
    const reordered = arrayMove(items, oldIndex, newIndex);
    setPlaylist(prev => ({ ...prev, items: reordered }));
    // Persist
    const itemIds = reordered.map(i => i.item_id);
    await axios.put(`${API_URL}/teacher/playlists/${id}/reorder`, { item_ids: itemIds });
  };

  const saveLabel = async (itemId) => {
    await axios.put(`${API_URL}/teacher/playlists/${id}/items/${itemId}`, {
      custom_label: labelValue || null,
    });
    setEditingLabel(null);
    await fetchPlaylist();
  };

  const savePlaylistName = async () => {
    if (!nameValue.trim()) return;
    await axios.put(`${API_URL}/teacher/playlists/${id}`, { name: nameValue.trim() });
    setEditingName(false);
    await fetchPlaylist();
  };

  const saveDescription = async () => {
    await axios.put(`${API_URL}/teacher/playlists/${id}`, { description: descValue.trim() });
    setEditingDesc(false);
    await fetchPlaylist();
  };

  const launchPreset = (preset) => {
    const c = preset.config || {};
    const params = new URLSearchParams(c);
    navigate(`/play/${preset.game}/setup?${params}`);
  };

  if (loading) return (
    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ fontFamily: 'Nunito, sans-serif', color: '#94A3B8' }}>Loading...</div>
    </div>
  );
  if (!playlist) return null;

  const items = playlist.items || [];

  return (
    <div data-testid="playlist-detail-page" style={{ height: '100%', overflow: 'auto', padding: '2rem 2.5rem', background: '#F8F9FB' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem' }}>
        <button
          data-testid="playlist-back-btn"
          onClick={() => navigate('/dashboard')}
          style={{
            background: 'none', border: 'none', cursor: 'pointer', color: '#64748B',
            padding: '0.25rem', display: 'flex', alignItems: 'center',
          }}
        >
          <ArrowLeft size={20} />
        </button>
        {editingName ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              data-testid="playlist-name-input"
              value={nameValue}
              onChange={e => setNameValue(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && savePlaylistName()}
              autoFocus
              style={{
                fontFamily: 'Outfit, sans-serif', fontSize: '1.5rem', fontWeight: 700,
                color: '#1A1D20', border: '1px solid #4C6EF5', borderRadius: '0.5rem',
                padding: '0.25rem 0.75rem', outline: 'none', background: 'white',
              }}
            />
            <button onClick={savePlaylistName} style={iconBtnStyle}><Check size={18} color="#2F9E44" /></button>
            <button onClick={() => setEditingName(false)} style={iconBtnStyle}><X size={18} color="#E03131" /></button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <h1 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.5rem', fontWeight: 700, color: '#1A1D20', margin: 0 }}>
              {playlist.name}
            </h1>
            <button
              data-testid="playlist-edit-name-btn"
              onClick={() => { setNameValue(playlist.name); setEditingName(true); }}
              style={{ ...iconBtnStyle, color: '#94A3B8' }}
            >
              <Pencil size={16} />
            </button>
          </div>
        )}
      </div>
      <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.875rem', color: '#94A3B8', margin: '0 0 1rem 2.5rem' }}>
        {items.length} item{items.length !== 1 ? 's' : ''} in sequence
      </p>

      {/* Description */}
      <div style={{ margin: '0 0 1.5rem 2.5rem', maxWidth: 600 }}>
        {editingDesc ? (
          <div data-testid="playlist-desc-editor" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <textarea
              data-testid="playlist-desc-input"
              value={descValue}
              onChange={e => setDescValue(e.target.value)}
              placeholder="Add a description for this playlist..."
              autoFocus
              rows={3}
              style={{
                fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#1A1D20',
                border: '1px solid #4C6EF5', borderRadius: '0.5rem',
                padding: '0.5rem 0.75rem', outline: 'none', background: 'white',
                resize: 'vertical', lineHeight: 1.5,
              }}
            />
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              <button
                data-testid="playlist-desc-save"
                onClick={saveDescription}
                style={{
                  padding: '0.3rem 0.75rem', borderRadius: '0.375rem',
                  background: '#1E2140', color: 'white', border: 'none',
                  fontFamily: 'Nunito, sans-serif', fontSize: '0.775rem', fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Save
              </button>
              <button
                onClick={() => setEditingDesc(false)}
                style={{
                  padding: '0.3rem 0.75rem', borderRadius: '0.375rem',
                  background: 'transparent', color: '#94A3B8', border: '1px solid #E2E8F0',
                  fontFamily: 'Nunito, sans-serif', fontSize: '0.775rem', fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : playlist.description ? (
          <div
            data-testid="playlist-desc-display"
            onClick={() => { setDescValue(playlist.description); setEditingDesc(true); }}
            style={{
              fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#475569',
              lineHeight: 1.6, cursor: 'pointer', padding: '0.4rem 0.6rem',
              borderRadius: '0.5rem', transition: 'background 0.15s',
              border: '1px solid transparent',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#F1F5F9'; e.currentTarget.style.borderColor = '#E2E8F0'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'transparent'; }}
          >
            {playlist.description}
          </div>
        ) : (
          <button
            data-testid="playlist-add-desc-btn"
            onClick={() => { setDescValue(''); setEditingDesc(true); }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
              background: 'none', border: 'none', cursor: 'pointer',
              fontFamily: 'Nunito, sans-serif', fontSize: '0.825rem', fontWeight: 600,
              color: '#94A3B8', padding: '0.25rem 0',
            }}
            onMouseEnter={e => e.currentTarget.style.color = '#4C6EF5'}
            onMouseLeave={e => e.currentTarget.style.color = '#94A3B8'}
          >
            <FileText size={14} /> Add description
          </button>
        )}
      </div>

      {/* Add Presets Button */}
      <button
        data-testid="playlist-add-presets-btn"
        onClick={() => { fetchPresets(); setShowAddModal(true); }}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
          padding: '0.625rem 1.25rem', borderRadius: '0.625rem',
          background: '#1E2140', color: 'white', border: 'none',
          fontFamily: 'Outfit, sans-serif', fontSize: '0.875rem', fontWeight: 600,
          cursor: 'pointer', marginBottom: '1.5rem',
          transition: 'transform 0.15s',
        }}
        onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-1px)'}
        onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
      >
        <Plus size={16} /> Add Presets
      </button>

      {/* Sequence Items with Drag-to-Reorder */}
      {items.length === 0 ? (
        <div data-testid="playlist-empty" style={{
          textAlign: 'center', padding: '3rem 2rem', background: 'white',
          borderRadius: '1rem', border: '1px solid #E2E8F0',
        }}>
          <ListOrdered size={40} color="#94A3B8" style={{ marginBottom: '0.75rem' }} />
          <p style={{ fontFamily: 'Nunito, sans-serif', color: '#64748B', fontSize: '0.925rem' }}>
            No presets added yet. Click "Add Presets" to build your sequence.
          </p>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext items={items.map(i => i.item_id)} strategy={verticalListSortingStrategy}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {items.map((item, index) => (
                <SortableItem
                  key={item.item_id}
                  item={item}
                  index={index}
                  totalItems={items.length}
                  expandedItem={expandedItem}
                  setExpandedItem={setExpandedItem}
                  editingLabel={editingLabel}
                  setEditingLabel={setEditingLabel}
                  labelValue={labelValue}
                  setLabelValue={setLabelValue}
                  saveLabel={saveLabel}
                  removeItem={removeItem}
                  launchPreset={launchPreset}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Add Presets Modal */}
      {showAddModal && (
        <div
          data-testid="add-presets-modal"
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
          }}
          onClick={() => setShowAddModal(false)}
        >
          <div
            style={{
              background: 'white', borderRadius: '1rem', width: '90%', maxWidth: 520,
              maxHeight: '70vh', display: 'flex', flexDirection: 'column',
              boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{
              padding: '1.25rem 1.5rem', borderBottom: '1px solid #F1F5F9',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.1rem', fontWeight: 600, margin: 0 }}>
                Add Presets to Sequence
              </h3>
              <button data-testid="add-presets-modal-close" onClick={() => setShowAddModal(false)} style={iconBtnStyle}>
                <X size={20} color="#64748B" />
              </button>
            </div>
            <div style={{ padding: '1rem 1.5rem', overflow: 'auto', flex: 1 }}>
              {presets.length === 0 ? (
                <p style={{ fontFamily: 'Nunito, sans-serif', color: '#94A3B8', textAlign: 'center', padding: '2rem 0' }}>
                  No saved presets yet. Save a game configuration first.
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {presets.map(p => (
                    <div
                      key={p._id}
                      data-testid={`add-preset-option-${p._id}`}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '0.75rem 1rem', borderRadius: '0.625rem',
                        border: '1px solid #E2E8F0', background: 'white',
                      }}
                    >
                      <div>
                        <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 700, color: '#1A1D20', fontSize: '0.875rem' }}>
                          {p.name}
                        </div>
                        <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.75rem', color: '#94A3B8' }}>
                          {GAME_LABELS[p.game] || p.game}
                        </div>
                      </div>
                      <button
                        data-testid={`add-preset-btn-${p._id}`}
                        onClick={async () => { await addItem(p._id); }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '0.35rem',
                          padding: '0.375rem 0.875rem', borderRadius: '0.5rem',
                          background: '#F0F4FF', color: '#4C6EF5', border: 'none',
                          fontFamily: 'Nunito, sans-serif', fontSize: '0.775rem', fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        <Plus size={12} /> Add
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailChip({ label, value }) {
  return (
    <div>
      <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.7rem', fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </div>
      <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', fontWeight: 600, color: '#1A1D20' }}>
        {value}
      </div>
    </div>
  );
}

const iconBtnStyle = {
  background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
